import React from 'react';

interface ConfidenceData {
  signals: {
    ord_precedent: { count: number; method: string };
    patent_exposure: { zone: number | null; hits: number; families: number };
    conditions: {
      has_solvent: boolean;
      has_temperature: boolean;
      has_time: boolean;
      has_yield: boolean;
      yield_percent: number | null;
      has_catalyst: boolean;
      score: number;
    };
    biology: { targets_found: number; relevant: boolean };
  };
}

type Status = 'good' | 'warn' | 'bad' | 'off';

interface SignalRow {
  status: Status;
  label: string;
  detail: string;
  value: number; // 0–1 for the bar
}

const statusColors: Record<Status, { dot: string; border: string; bar: string }> = {
  good: { dot: 'bg-emerald-500', border: 'border-l-emerald-500', bar: 'bg-emerald-500' },
  warn: { dot: 'bg-amber-500', border: 'border-l-amber-500', bar: 'bg-amber-500' },
  bad:  { dot: 'bg-red-500', border: 'border-l-red-500', bar: 'bg-red-500' },
  off:  { dot: 'bg-gray-300', border: 'border-l-gray-300', bar: 'bg-gray-300' },
};

function buildSignals(data: ConfidenceData): SignalRow[] {
  const s = data.signals;
  const rows: SignalRow[] = [];

  // 1. Published Evidence
  const ordCount = s.ord_precedent.count;
  if (ordCount >= 3) {
    rows.push({ status: 'good', label: 'Published Evidence', detail: `Well-documented (${ordCount} matches in ORD)`, value: 1 });
  } else if (ordCount >= 1) {
    rows.push({ status: 'warn', label: 'Published Evidence', detail: `Limited evidence (${ordCount} match${ordCount > 1 ? 'es' : ''} in ORD)`, value: 0.4 });
  } else {
    rows.push({ status: 'bad', label: 'Published Evidence', detail: 'No published evidence found', value: 0 });
  }

  // 2. Patent Exposure
  const families = s.patent_exposure.families;
  const hits = s.patent_exposure.hits;
  if (hits === 0 && families === 0) {
    rows.push({ status: 'off', label: 'Patent Exposure', detail: 'No patent data available', value: 0 });
  } else if (families <= 10) {
    rows.push({ status: 'good', label: 'Patent Exposure', detail: `Low exposure (${families} patent families)`, value: 0.15 });
  } else if (families <= 50) {
    rows.push({ status: 'warn', label: 'Patent Exposure', detail: `Moderate exposure (${families} patent families)`, value: 0.5 });
  } else {
    rows.push({ status: 'warn', label: 'Patent Exposure', detail: `Significant exposure (${families} families) — review recommended`, value: Math.min(families / 200, 1) });
  }

  // 3. Conditions completeness
  const cond = s.conditions;
  const maxScore = 6;
  const missing: string[] = [];
  if (!cond.has_solvent) missing.push('solvent');
  if (!cond.has_temperature) missing.push('temperature');
  if (!cond.has_time) missing.push('time');
  if (!cond.has_catalyst) missing.push('catalyst');
  if (!cond.has_yield) missing.push('yield');

  if (cond.score >= 5) {
    rows.push({ status: 'good', label: 'Conditions', detail: 'All conditions specified', value: cond.score / maxScore });
  } else if (cond.score >= 3) {
    rows.push({ status: 'warn', label: 'Conditions', detail: `Partial — missing ${missing.join(', ')}`, value: cond.score / maxScore });
  } else {
    rows.push({ status: 'bad', label: 'Conditions', detail: `Incomplete — missing ${missing.join(', ')}`, value: cond.score / maxScore });
  }

  // 4. Yield
  const yld = cond.yield_percent;
  if (yld == null) {
    rows.push({ status: 'off', label: 'Yield', detail: 'No yield reported', value: 0 });
  } else if (yld >= 80) {
    rows.push({ status: 'good', label: 'Yield', detail: `${yld}%`, value: yld / 100 });
  } else if (yld >= 50) {
    rows.push({ status: 'warn', label: 'Yield', detail: `${yld}%`, value: yld / 100 });
  } else {
    rows.push({ status: 'bad', label: 'Yield', detail: `Low yield: ${yld}%`, value: yld / 100 });
  }

  // 5. Biology
  const bio = s.biology;
  if (!bio.relevant && bio.targets_found === 0) {
    rows.push({ status: 'off', label: 'Biology', detail: 'Not connected', value: 0 });
  } else {
    rows.push({ status: 'good', label: 'Biology', detail: `${bio.targets_found} biological target${bio.targets_found !== 1 ? 's' : ''} found`, value: 1 });
  }

  return rows;
}

const SignalRowComponent: React.FC<{ row: SignalRow }> = ({ row }) => {
  const colors = statusColors[row.status];
  return (
    <div className={`flex items-center gap-3 px-3 py-2 border-l-[3px] ${colors.border}`}>
      {/* Status dot */}
      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${colors.dot}`} />

      {/* Label */}
      <span className="text-xs font-semibold text-gray-700 w-24 shrink-0">{row.label}</span>

      {/* Detail */}
      <span className="text-xs text-gray-500 flex-1 min-w-0 truncate" title={row.detail}>
        {row.detail}
      </span>

      {/* Mini bar */}
      <div className="w-16 h-1.5 bg-gray-100 rounded-full shrink-0 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${colors.bar}`}
          style={{ width: `${Math.max(row.value * 100, row.status === 'off' ? 0 : 4)}%` }}
        />
      </div>
    </div>
  );
};

interface ConfidenceSignalCardProps {
  confidence: ConfidenceData;
  stringencyMode: 'strict' | 'standard' | 'exploratory';
}

const ConfidenceSignalCard: React.FC<ConfidenceSignalCardProps> = ({ confidence, stringencyMode }) => {
  const rows = buildSignals(confidence);
  const modeData = (confidence as any).modes?.[stringencyMode];

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Summary */}
      {modeData?.summary && (
        <div className="px-6 py-4 border-b border-gray-50">
          <p className="text-sm text-gray-600">{modeData.summary}</p>
        </div>
      )}

      {/* Signals */}
      <details className="group">
        <summary className="px-6 py-3 text-xs text-gray-400 cursor-pointer hover:text-gray-600 font-medium select-none flex items-center gap-1">
          <svg className="w-3 h-3 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Signal breakdown
        </summary>
        <div className="pb-3 px-3 space-y-0.5">
          {rows.map((row) => (
            <SignalRowComponent key={row.label} row={row} />
          ))}
        </div>
      </details>
    </div>
  );
};

export default ConfidenceSignalCard;
